import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import type { FilterName } from '@/stores/useFilterStoreTypes';
import type { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import type TitleListGraphqlType from '@/components/list/TitleList.vue';

import { FilterBar, TitleList } from '@/lazy-load-components';

import ContentHeader from '@/components/content-header/ContentHeader.vue';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import Spinner from '@/ui-components/Spinner.vue';

import { CollectionType } from '@/enums/collection-type';
import { TitleOrder } from '@/enums/title-order';
import { TitleListLayout } from '@/enums/title-list-layout';
import { PopularTitlesSorting, TitleFilter, SraPlacement } from '@/@types/graphql-types';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { toGraphqlFilters } from '@/helpers/graphql-model-helper';

import {
	GetSearchTitlesDocument,
	GetSearchTitlesQuery,
	GetSearchTitlesQueryVariables,
} from '@/pages/graphql/queries/GetSearchTitles.query';

import { toastError } from '@/helpers/vfm/toast-helper';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';
import { useSearchTracking } from '@/features/search/tracking';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'Search',
  setup(__props) {

const FilterBarSeo = () =>
	import(/* webpackChunkName: "filter-bar-seo" */ '@/components/filter-bar/seo/FilterBarSeo.vue');
const TitleListOptionsSeo = () =>
	import(/* webpackChunkName: "title-list-options-seo" */ '@/components/filter-bar/seo/TitleListOptionsSeo.vue');

const { filters, resetFilters, selectedProviders } = useFilterStore();
const filtersSearch = computed(() => filters(CollectionType.SEARCH));

const { language, country } = storeToRefs(useLanguageStore());

const { queryParams, activeRoute, lastRoute } = storeToRefs(useRoutingStore());

const { isLoggedIn } = storeToRefs(useUserStore());

const isSearchView = ref(activeRoute.value?.name === 'app.search.list');
const isFirstPageLoaded = ref(false);
const listLayout = ref<TitleListLayout>(TitleListLayout.ROW);

const showFooter = ref(false);

const isSearchCollection = computed(() => activeRoute.value?.meta?.collectionType === CollectionType.SEARCH);

const query = ref(activeRoute.value?.query?.q ?? '');
const isFiltersVisible = ref(false);

const titleList = ref<InstanceType<typeof TitleListGraphqlType> | null>(null);

/* We need a way to watch for filter changes AND query changes, since q and person_id parameters are not tracked in any store.*/
watch([isFiltersVisible, query, () => filtersSearch.value], () => {
	titleList.value?.resetInfiniteScroll();

	isFirstPageLoaded.value = false;

	// scroll to top if the filters have changed
	window.scrollTo(0, 0);
});

const graphqlFilters = computed<TitleFilter>(() =>
	toGraphqlFilters(filtersSearch.value, selectedProviders(!isLoggedIn.value))
);

const personId = computed(() => (activeRoute.value?.query.person_id ? `tp${activeRoute.value.query.person_id}` : null));

// Sort By options from GraphQL are Uppercase, but tracking expects lowercase TODO [Graphql] remove when get rid of REST
const RestToGraphQLSortingMap: Partial<{ [key in TitleOrder]: PopularTitlesSorting }> = {
	[TitleOrder.POPULARITY]: PopularTitlesSorting.Popular,
	[TitleOrder.TRENDING]: PopularTitlesSorting.Trending,
	[TitleOrder.TMDB_POPULARITY]: PopularTitlesSorting.TmdbPopularity,
	[TitleOrder.ALPHABETICAL]: PopularTitlesSorting.Alphabetical,
	[TitleOrder.RANDOM]: PopularTitlesSorting.Random,
	[TitleOrder.RELEASE_YEAR]: PopularTitlesSorting.ReleaseYear,
	[TitleOrder.RELEVANCE]: PopularTitlesSorting.Relevance,
	[TitleOrder.SCORE_IMDB]: PopularTitlesSorting.ImdbScore,
	[TitleOrder.RUNTIME]: PopularTitlesSorting.Runtime,
};
const sortBy = computed<PopularTitlesSorting>(() => {
	// To fetch titles on server side we need to get sort by option from url params
	if (process.server) {
		const currentSortBy = queryParams.value.sort_by as TitleOrder;
		return RestToGraphQLSortingMap[currentSortBy] ?? PopularTitlesSorting.Popular;
	}

	if (filtersSearch.value.sort_by) {
		return RestToGraphQLSortingMap[filtersSearch.value.sort_by as TitleOrder] ?? PopularTitlesSorting.Popular;
	}

	return PopularTitlesSorting.Popular;
});

const randomSeed = computed(() => {
	const isRandomSort = sortBy.value === PopularTitlesSorting.Random;

	if (process.server) {
		const randomSeed = Number(queryParams.value.sorting_random_seed) || 1;
		return isRandomSort ? randomSeed : 0;
	}

	const randomSeed = filtersSearch.value.sorting_random_seed || 1;
	return isRandomSort ? randomSeed : 0;
});

const queryVariables = computed<GetSearchTitlesQueryVariables>(() => ({
	searchAfterCursor: '',
	searchTitlesFilter: {
		...(isFiltersVisible.value || isLoggedIn.value ? graphqlFilters.value : {}),
		searchQuery: personId.value ? null : query.value,
		personId: personId.value ?? null,
		includeTitlesWithoutUrl: true,
	},
	searchTitlesSortBy: sortBy.value,
	language: language.value,
	country: country.value,
	sortRandomSeed: randomSeed.value,
	location: 'SearchPage',
}));

const {
	result: searchPageData,
	loading: isTitlesLoading,
	fetchMore,
	onError,
	onResult,
} = useQuery<GetSearchTitlesQuery, GetSearchTitlesQueryVariables>(GetSearchTitlesDocument, queryVariables);

onResult(() => (isFirstPageLoaded.value = true));

const { tc } = useI18n();
onError(error => {
	captureMessageForSentry('[GraphQL Get Search Page Data]:', { error, where: 'Component: Search' }, 'error');

	toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
});

const searchTitles = computed(() => searchPageData.value?.searchTitles.edges ?? []);

const titleAmount = computed(() => searchPageData.value?.searchTitles.totalCount ?? 0);

const isFirstPage = computed(() => !searchPageData.value?.searchTitles.pageInfo?.hasPreviousPage);

const isAtEndOfList = computed(() => !searchPageData.value?.searchTitles.pageInfo?.hasNextPage);

const personBackdrop = computed(() => {
	const titleWithBackdrop = searchTitles.value.find(edge => edge.node.content?.backdrops?.[0]?.backdropUrl);

	if (titleWithBackdrop) {
		return titleWithBackdrop.node.content.backdrops[0].backdropUrl;
	}

	return null;
});
const firstSearchTitle = computed(() => searchTitles.value[0]?.node ?? null);

function onLoadNext({ event }: { event: any }) {
	if (event == null) return;

	const searchTitlesFetchMore = fetchMore({
		variables: {
			searchAfterCursor: searchPageData.value?.searchTitles.pageInfo.endCursor,
		},
		updateQuery(previousResult, { fetchMoreResult }) {
			if (!fetchMoreResult) return previousResult;

			const previousEdges = previousResult.searchTitles.edges ?? [];
			const fetchMoreEdges = fetchMoreResult.searchTitles.edges ?? [];

			return {
				searchTitles: {
					__typename: previousResult.searchTitles.__typename,
					totalCount: fetchMoreResult.searchTitles.totalCount ?? 0,
					edges: [...previousEdges, ...fetchMoreEdges],
					pageInfo: fetchMoreResult.searchTitles.pageInfo!,
				},
			};
		},
	});

	if (searchTitlesFetchMore == null) return;
	searchTitlesFetchMore
		.catch(error => {
			captureMessageForSentry(
				'[GraphQL Search Page Load More Titles Issue]:',
				{ error, where: 'Component: Search, method: onLoadNext' },
				'error'
			);
		})
		.finally(() => event.target?.complete?.(searchTitles.value.length));
}

watch(
	() => activeRoute.value,
	() => {
		if (isSearchCollection.value) {
			if (lastRoute.value?.meta?.collectionType !== CollectionType.SEARCH) {
				resetFilters({
					filterNames: Object.keys(filtersSearch.value) as FilterName[],
					updateRouteState: false,
				});
			}

			query.value = activeRoute.value?.query.q as string;
		}
	}
);

// Sponsored Recommendation
const { sponsoredAd, isSRLoaded } = useSendBidId({
	pageType: 'VIEW_SEARCH',
	placement: SraPlacement.SearchPage,
	isEnabled: () => isFirstPageLoaded && firstSearchTitle.value?.id != null,
	jwEntityID: () => firstSearchTitle.value?.id,
});

const { trackSearch } = useSearchTracking();
function trackClick(payload: TitleEventPayloadGraphql) {
	const titleObjectId = payload.title.show?.objectId ?? payload.title.objectId;
	const titleObjectType = payload.title.show?.objectType ?? payload.title.objectType;
	const titleContext = new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		null,
		null,
		null,
		payload.index
	);

	trackSearch('clickTitle', { spec: { label: 'results' }, contexts: [titleContext] });
}

return { __sfc: true,FilterBarSeo, TitleListOptionsSeo, filters, resetFilters, selectedProviders, filtersSearch, language, country, queryParams, activeRoute, lastRoute, isLoggedIn, isSearchView, isFirstPageLoaded, listLayout, showFooter, isSearchCollection, query, isFiltersVisible, titleList, graphqlFilters, personId, RestToGraphQLSortingMap, sortBy, randomSeed, queryVariables, searchPageData, isTitlesLoading, fetchMore, onError, onResult, tc, searchTitles, titleAmount, isFirstPage, isAtEndOfList, personBackdrop, firstSearchTitle, onLoadNext, sponsoredAd, isSRLoaded, trackSearch, trackClick, FilterBar, TitleList, ContentHeader, SponsoredRecommendation, Spinner, CollectionType }
}

})